import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  loginRequest,
  signupRequest,
  resetPasswordRequest,
} from "../utils/authApi";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem("auth_user");
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(
    () => localStorage.getItem("auth_token") || null
  );

  useEffect(() => {
    if (token) {
      localStorage.setItem("auth_token", token);
    } else {
      localStorage.removeItem("auth_token");
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem("auth_user", JSON.stringify(user));
    } else {
      localStorage.removeItem("auth_user");
    }
  }, [user]);

  const login = useCallback(async (username, password) => {
    const data = await loginRequest(username, password);
    // Match Flutter UserModel: token is under 'access_token'
    const maybeToken =
      data?.access_token ||
      data?.token ||
      data?.data?.token ||
      data?.data?.[0]?.token;
    if (!maybeToken) throw new Error("Token not found in response");
    setToken(maybeToken);
    setUser(data);
    return data;
  }, []);

  const signup = useCallback(async (email, password, userName, phone) => {
    const data = await signupRequest(email, password, userName, phone);
    return data;
  }, []);

  const resetPassword = useCallback(async (email) => {
    const data = await resetPasswordRequest(email);
    return data;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
  }, []);

  const value = useMemo(
    () => ({ user, token, login, signup, resetPassword, logout }),
    [user, token, login, signup, resetPassword, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
